import React, { createContext, useContext, useState } from 'react';
import { UserService } from '../configuracoes/usuario/Service';

const userService = new UserService();

interface Params {
    data: any[];
    loading: boolean;
    error: boolean;
    filters: {
        search: string;
        grupoId: string;
        atendenteId: string;
        selfSchedulingStatus: string;
    };
    setFilters: (filters: Partial<{ search: string; grupoId: string; atendenteId: string; selfSchedulingStatus: string }>) => void;
    loadClients: (search?: string, grupoId?: string, atendenteId?: string) => Promise<void>;
}

const Context = createContext<Params | undefined>(undefined);

export const useClientsContext = () => {
    const context = useContext(Context);
    if (!context) {
        throw new Error('useParamsContext must be used within a ParamsProvider');
    }
    return context;
};

export const ClientsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [filters, setFiltersState] = useState({
        search: '',
        grupoId: '',
        atendenteId: '',
        selfSchedulingStatus: '',
    });

    const loadClients = async (search?: string, grupoId?: string, atendenteId?: string) => {
        try {
            setLoading(true);
            setError(false);

            const resolvedSearch = search !== undefined ? search : undefined;
            const resolvedGrupoId = grupoId !== undefined ? grupoId : filters.grupoId;
            const resolvedAtendenteId = atendenteId !== undefined ? atendenteId : filters.atendenteId;

            setFiltersState((prev) => ({
                search: search !== undefined ? search : prev.search,
                grupoId: resolvedGrupoId || '',
                atendenteId: resolvedAtendenteId || '',
                selfSchedulingStatus: prev.selfSchedulingStatus,
            }));

            const normalizedSearch = resolvedSearch?.trim() || undefined;
            const normalizedGrupoId = resolvedGrupoId?.trim() || undefined;
            const normalizedAtendenteId = resolvedAtendenteId?.trim() || undefined;

            const { data } = await userService.getClients(
                normalizedSearch,
                normalizedGrupoId,
                normalizedAtendenteId
            );

            setData(data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const setFilters = (
        nextFilters: Partial<{ search: string; grupoId: string; atendenteId: string; selfSchedulingStatus: string }>
    ) => {
        setFiltersState((prev) => ({
            search: nextFilters.search ?? prev.search,
            grupoId: nextFilters.grupoId ?? prev.grupoId,
            atendenteId: nextFilters.atendenteId ?? prev.atendenteId,
            selfSchedulingStatus: nextFilters.selfSchedulingStatus ?? prev.selfSchedulingStatus,
        }));
    };

    return <Context.Provider value={{ data, loadClients, loading, error, filters, setFilters }}>{children}</Context.Provider>;
};
